import { Button, ToastId, Toasts } from '@tnwinc/navex-react';
import React, { useState, useEffect, FC, useRef } from 'react';
import { IGetEmployeeCount, upsertEmployeeCount } from './EmployeeCountApi';

interface IProps {
    employeeCountInfo: IGetEmployeeCount[],
    refetch: () => void,
}

interface Row {
    year: number;
    quarter: string; // 'Q1', 'Q2', etc. or 'Year'
    count: number | string;
}

const quarterOrder = ['Q1', 'Q2', 'Q3', 'Q4'];

function getNextQuarter(year: number, quarter: string) {
    const idx = quarterOrder.indexOf(quarter);
    if (idx === -1) return { year, quarter: 'Q1' };
    if (idx === 3) return { year: year + 1, quarter: 'Q1' };
    return { year, quarter: quarterOrder[idx + 1] };
}
function getCurrentQuarterAndYear() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0-based
    const quarter = quarterOrder[Math.floor(month / 3)];
    return { year, quarter };
}

function getSortedRows(employeeCountInfo: IGetEmployeeCount[]): Row[] {
    const { year: currentYear, quarter: currentQuarter } = getCurrentQuarterAndYear();

    // If no data, show only current year row and current quarter row with empty count
    if (!employeeCountInfo || employeeCountInfo.length === 0) {
        return [
            { year: currentYear, quarter: '', count: '' },
            { year: currentYear, quarter: currentQuarter, count: '' }
        ];
    }

    const years = Array.from(new Set(employeeCountInfo.map(e => e.year))).sort((a, b) => b - a);
    const rows: Row[] = [];

    for (const year of years) {
        let quarters: Row[] = [];

        if (year === currentYear) {
            const dataQuarters = employeeCountInfo
                .filter(e => e.year === year && e.quarter)
                .map(e => e.quarter);

            const uniqueQuarters = Array.from(new Set([...dataQuarters, currentQuarter]));

            let lastQuarterCount: number | '' = '';
            const tempQuarters: Row[] = [];
            quarterOrder.forEach(q => {
                if (!uniqueQuarters.includes(q)) return;
                const quarterRow = employeeCountInfo.find(e => e.year === year && e.quarter === q);
                let count: number | '' = '';
                if (quarterRow && typeof quarterRow.count === 'number') {
                    count = quarterRow.count;
                    lastQuarterCount = count;
                } else if (q === currentQuarter) {
                    count = lastQuarterCount;
                }
                tempQuarters.push({ year, quarter: q, count });
            });
            quarters = tempQuarters.reverse();
        } else {
            quarters = quarterOrder
                .slice()
                .reverse()
                .map(q => {
                    const quarterRow = employeeCountInfo.find(e => e.year === year && e.quarter === q);
                    if (quarterRow && typeof quarterRow.count === 'number') {
                        return { year, quarter: q, count: quarterRow.count };
                    }
                    return null;
                })
                .filter(Boolean) as Row[];
        }

        // Year row: use DB value if exists, else empty
        const dbYearRow = employeeCountInfo.find(e => e.year === year && (!e.quarter || e.quarter === ''));
        rows.push({
            year,
            quarter: '',
            count: dbYearRow ? (dbYearRow.count ?? '') : ''
        });

        rows.push(...quarters);
    }

    return rows;
}

export const ManageEmployeeCount: FC<IProps> = (props) => {
    const [rows, setRows] = useState<Row[]>(() => getSortedRows(props.employeeCountInfo));
    const [errorMessage, setErrorMessage] = useState('');
    let toastId: ToastId = 0;
    const manualUpsertRef = useRef(false);

    const [upsert] = upsertEmployeeCount({
        onSuccess: () => {
            if (manualUpsertRef.current && !Toasts.isActive(toastId)) {
                toastId = Toasts.success('Updated Successfully');
            }
            props.refetch();
            manualUpsertRef.current = false; // Reset after successful upsert
        },
        onError: () => {
            if (manualUpsertRef.current && !Toasts.isActive(toastId)) {
                toastId = Toasts.alert("An error occurred processing your request. Please try again.");
            }
            manualUpsertRef.current = false; // Reset after upsert
        }
    });

    // Track upsert attempts to avoid infinite loop
    const upsertAttemptedRef = useRef(false);

    // Update rows when employeeCountInfo changes
    useEffect(() => {
        setRows(getSortedRows(props.employeeCountInfo));
        setErrorMessage('');
        // Reset upsert attempt flag when data changes
        upsertAttemptedRef.current = false;
    }, [props.employeeCountInfo]);

    // Upsert current quarter and year rows when component mounts, but only once per missing row
    useEffect(() => {
        if (!props.employeeCountInfo.length || upsertAttemptedRef.current) return;

        const { year: currentYear, quarter: currentQuarter } = getCurrentQuarterAndYear();
        const sortedRows = getSortedRows(props.employeeCountInfo);

        // Upsert current quarter row if not in DB and count is not empty
        const currentQuarterRow = sortedRows.find(
            row => row.year === currentYear && row.quarter === currentQuarter
        );
        const existsCurrentQuarter = props.employeeCountInfo.some(
            info => info.year === currentYear && info.quarter === currentQuarter
        );
        if (
            currentQuarterRow &&
            !existsCurrentQuarter &&
            typeof currentQuarterRow.count === 'number'

        ) {
            upsert({
                Id: 0,
                Quarter: currentQuarterRow.quarter,
                Year: currentQuarterRow.year,
                Count: currentQuarterRow.count,
            });
            upsertAttemptedRef.current = true;
        }

        // Upsert current year row if not in DB and count is not empty
        const currentYearRow = sortedRows.find(
            row => row.year === currentYear && row.quarter === ''
        );
        const existsCurrentYear = props.employeeCountInfo.some(
            info => info.year === currentYear && (!info.quarter || info.quarter === '')
        );
        if (
            currentYearRow &&
            !existsCurrentYear &&
            typeof currentYearRow.count === 'number'
        ) {
            upsert({
                Id: 0,
                Quarter: '',
                Year: currentYearRow.year,
                Count: currentYearRow.count,
            });
            upsertAttemptedRef.current = true;
        }
    }, [props.employeeCountInfo, upsert]);

    const handleUpdate = async () => {
        manualUpsertRef.current = true;
        const upserts = [];
        const seen = new Set(); // Prevent duplicate upserts

        // If there is no data, only upsert the current year row and current quarter row
        if (!props.employeeCountInfo || props.employeeCountInfo.length === 0) {
            const { year: currentYear, quarter: currentQuarter } = getCurrentQuarterAndYear();
            const yearRow = rows.find(row => row.year === currentYear && row.quarter === '');
            const quarterRow = rows.find(row => row.year === currentYear && row.quarter === currentQuarter);

            if (yearRow && typeof yearRow.count === 'number') {
                upserts.push(
                    upsert({
                        Id: 0,
                        Quarter: '',
                        Year: currentYear,
                        Count: yearRow.count,
                    })
                );
            }
            if (quarterRow && typeof quarterRow.count === 'number') {
                upserts.push(
                    upsert({
                        Id: 0,
                        Quarter: currentQuarter,
                        Year: currentYear,
                        Count: quarterRow.count,
                    })
                );
            }
        } else {
            // ...existing logic for when there is data...
            const yearSums: Record<number, number> = {};
            rows.forEach(row => {
                if (row.quarter && typeof row.count === 'number') {
                    yearSums[row.year] = (yearSums[row.year] || 0) + row.count;
                }
            });

            for (let i = 0; i < rows.length; i++) {
                const row = rows[i];
                const key = `${row.year}-${row.quarter}`;
                if (seen.has(key)) continue; // Skip duplicate
                seen.add(key);

                if (row.quarter === '') {
                    const match = props.employeeCountInfo.find(
                        info => info.year === row.year && (!info.quarter || info.quarter === '')
                    );
                    if (
                        typeof row.count === 'number' &&
                        (!match || match.count !== row.count)
                    ) {
                        upserts.push(
                            upsert({
                                Id: match ? match.id : 0,
                                Quarter: '',
                                Year: row.year,
                                Count: row.count,
                            })
                        );
                    }
                }
                else if (typeof row.count === 'number') {
                    const match = props.employeeCountInfo.find(
                        info => info.year === row.year && info.quarter === row.quarter
                    );
                    if (!match || match.count !== row.count) {
                        upserts.push(
                            upsert({
                                Id: match ? match.id : 0,
                                Quarter: row.quarter,
                                Year: row.year,
                                Count: row.count,
                            })
                        );
                    }
                }
            }
        }

        if (upserts.length > 0) {
            await Promise.all(upserts);
            props.refetch();
        }
    };

    const handleCountChange = (idx: number, value: string) => {
        setErrorMessage('');
        const numericValue = value.replace(/,/g, '');

        if (!validateCount(value, numericValue)) {
            setErrorMessage('Value should be numbers only');
            return;
        }

        setRows(prev => updateRowsWithYearSum(prev, idx, numericValue, value));
    };

    const handleCancel = () => {
        setRows(getSortedRows(props.employeeCountInfo));
        setErrorMessage('');
    };

    const validateCount = (value: string, numericValue: string): boolean => {
        return value === '' || /^\d+$/.test(numericValue);
    };

    const updateRowsWithYearSum = (
        rows: Row[],
        idx: number,
        numericValue: string,
        value: string
    ): Row[] => {
        return rows.map((row, i) =>
            i === idx
                ? { ...row, count: value === '' ? '' : Number(numericValue) }
                : row
        );
    };

    return (
        <div style={{ margin: '20px auto', fontFamily: 'Arial' }}>
            <table id="employeeCountTable" className="table table-striped table-bordered">
                <thead>
                    <tr>
                        <th style={{ padding: '8px' }}>Year and Quarter</th>
                        <th style={{ padding: '8px' }}>Employee Count
                            {errorMessage && (
                                <label className="control-label" htmlFor="employeeCount" style={{ marginBottom: 0 }}>
                                    <span className="validation-required">{" *  "}</span>
                                    <span className="validation-label" aria-hidden="false">{errorMessage}</span>
                                </label>
                            )}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((item, idx) => (
                        <tr key={`${item.year}-${item.quarter}`}>
                            <td style={{ padding: '8px', width: '200px', fontWeight: item.quarter === '' ? 'bold' : 'normal' }}>
                                {item.quarter === '' ? item.year : `${item.year} ${item.quarter}`}
                            </td>
                            <td style={{ padding: '8px' }}>
                                <input style={{ width: '110px' }}
                                    className="form-control no-ie-clear field"
                                    type="text"
                                    value={item.count}
                                    onChange={e => handleCountChange(idx, e.target.value)}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div style={{ position: "absolute", padding: '0px 175px' }}>
                <Button
                    id={`btnCancel`}
                    purpose="default"
                    style={{ margin: '10px' }}
                    onClick={handleCancel}>
                    Cancel
                </Button>
                <Button
                    id={`btnSave`}
                    purpose="primary"
                    type="submit"
                    onClick={handleUpdate}>
                    Update
                </Button>
            </div>
        </div>
    );
};